import { ChangeDetectorRef, Component, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, finalize, forkJoin } from 'rxjs';
import { PermissionDefinition, Role, User } from '../../core/models/user.models';
import { RolesService } from '../../core/services/roles.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { UsersService } from '../../core/services/users.service';
import { BusinessService, BusinessStore } from '../../core/services/business.service';
import { httpErrorMessage } from '../../core/utils/http-error-message';
import { Router } from '@angular/router';
import { CurrentUserService } from '../../core/auth/current-user.service';

@Component({
  selector: 'app-users',
  imports: [FormsModule],
  templateUrl: './users.html',
  styleUrl: './users.scss',
})
export class Users implements OnInit {
  private readonly usersService = inject(UsersService);
  private readonly rolesService = inject(RolesService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly businessService = inject(BusinessService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly currentUser = inject(CurrentUserService);

  get canInviteUsers(): boolean { return this.currentUser.hasPermission('USER_INVITE') || this.currentUser.hasPermission('USER_CREATE'); }
  get canUpdateUsers(): boolean { return this.currentUser.hasPermission('USER_UPDATE'); }
  get canDeleteUsers(): boolean { return this.currentUser.hasPermission('USER_DELETE'); }
  get canAssignRoles(): boolean { return this.currentUser.hasPermission('ROLE_ASSIGN'); }

  get isPlatformAdminView(): boolean {
    return this.router.url.startsWith('/admin');
  }

  invite = {
    name: '',
    email: '',
    roleIds: [] as string[],
    businessLocationIds: [] as string[],
    accessExpiresAt: '',
  };

  roles = ['Owner', 'Admin', 'Manager', 'Inventory Lead', 'Cashier'];
  roleRecords: Role[] = [];
  locations: BusinessStore[] = [];
  users: User[] = [];

  isLoading = false;
  isSubmitting = false;
  isLocationsLoading = false;
  errorMessage = '';
  successMessage = '';
  userPageNumber = 1;
  readonly userPageSize = 10;
  totalUserCount = 0;
  isLoadingMore = false;
  private usersPageExhausted = false;
  processingUserId = '';
  deletingUser: User | null = null;
  deleteConfirmationText = '';
  accessEditingUserId = '';
  accessExpiryValue = '';
  editingUserId = '';
  editingLocationIds: string[] = [];
  editingRoleIds: string[] = [];
  adminStatusFilter: 'all' | 'active' | 'inactive' = 'all';
  permissionRole: Role | null = null;
  rolePermissions: PermissionDefinition[] = [];
  permissionDefinitions: PermissionDefinition[] = [];
  selectedRolePermissionIds = new Set<string>();
  isRolePermissionsLoading = false;
  isEditingRolePermissions = false;
  isSavingRolePermissions = false;
  private readonly rolePermissionCache = new Map<string, PermissionDefinition[]>();

  get managesPlatformUsers(): boolean {
    return this.isPlatformAdminView;
  }

  get hasMoreUsers(): boolean {
    return !this.usersPageExhausted && this.users.length < this.totalUserCount;
  }

  get editingUser(): User | undefined {
    return this.users.find((user) => user.id === this.editingUserId);
  }

  get groupedRolePermissions(): Array<{ module: string; permissions: PermissionDefinition[] }> {
    const groups = new Map<string, PermissionDefinition[]>();
    for (const permission of this.rolePermissions) {
      const module = permission.module || 'Other';
      groups.set(module, [...(groups.get(module) ?? []), permission]);
    }
    return [...groups.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([module, permissions]) => ({
        module,
        permissions: permissions.sort((left, right) => left.name.localeCompare(right.name)),
      }));
  }

  get groupedEditablePermissions(): Array<{ module: string; permissions: PermissionDefinition[] }> {
    const groups = new Map<string, PermissionDefinition[]>();
    for (const permission of this.permissionDefinitions) {
      const module = permission.module || 'Other';
      groups.set(module, [...(groups.get(module) ?? []), permission]);
    }
    return [...groups.entries()].sort(([left], [right]) => left.localeCompare(right))
      .map(([module, permissions]) => ({ module, permissions }));
  }

  get canEditPermissionRole(): boolean {
    return Boolean(this.permissionRole) && (this.isPlatformAdminView || !this.permissionRole?.isSystemRole);
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.hasMoreUsers || this.isLoading || this.isLoadingMore) return;
    if (document.documentElement.scrollHeight - (window.scrollY + window.innerHeight) <= 320)
      this.loadMoreUsers();
  }

  @HostListener('document:keydown.escape')
  closePermissionsOnEscape(): void {
    this.closeRolePermissions();
  }

  ngOnInit(): void {
    this.loadUsers();
    if (this.isPlatformAdminView) {
      this.loadRoles();
    } else {
      this.loadRoles();
      this.loadLocations();
    }
  }

  loadLocations(): void {
    this.isLocationsLoading = true;
    this.businessService
      .stores('business', true)
      .pipe(finalize(() => {
        this.isLocationsLoading = false;
        this.renderNow();
      }))
      .subscribe({
        next: (locations) => {
          this.locations = locations;
          this.renderNow();
        },
        error: (error: unknown) => {
          this.locations = [];
          this.errorMessage = httpErrorMessage(error, 'Unable to load business locations.');
          this.renderNow();
        },
      });
  }

  toggleLocation(locationId: string, checked: boolean): void {
    this.invite.businessLocationIds = checked
      ? Array.from(new Set([...this.invite.businessLocationIds, locationId]))
      : this.invite.businessLocationIds.filter((id) => id !== locationId);
  }

  toggleInviteRole(roleId: string, checked: boolean): void {
    this.invite.roleIds = checked
      ? Array.from(new Set([...this.invite.roleIds, roleId]))
      : this.invite.roleIds.filter((id) => id !== roleId);
  }

  toggleEditingRole(roleId: string, checked: boolean): void {
    this.editingRoleIds = checked
      ? Array.from(new Set([...this.editingRoleIds, roleId]))
      : this.editingRoleIds.filter((id) => id !== roleId);
  }

  openRolePermissions(event: Event, role: Role): void {
    event.preventDefault();
    event.stopPropagation();
    this.permissionRole = role;
    this.rolePermissions = this.rolePermissionCache.get(role.id) ?? [];
    this.selectedRolePermissionIds = new Set(this.rolePermissions.map((permission) => permission.id));
    this.isEditingRolePermissions = false;
    this.isRolePermissionsLoading = true;
    this.renderNow();

    forkJoin({
      permissionIds: this.rolesService.permissionIds(role.id),
      definitions: this.permissionsService.list(),
    }).subscribe({
      next: ({ permissionIds, definitions }) => {
        const selectedIds = new Set(permissionIds);
        const permissions = definitions.filter((permission) => selectedIds.has(permission.id));
        this.permissionDefinitions = definitions.filter((permission) => this.isPlatformAdminView || !permission.code.startsWith('PLATFORM_'));
        this.selectedRolePermissionIds = selectedIds;
        this.rolePermissionCache.set(role.id, permissions);
        if (this.permissionRole?.id === role.id) this.rolePermissions = permissions;
        this.isRolePermissionsLoading = false;
        this.renderNow();
      },
      error: (error: unknown) => {
        this.isRolePermissionsLoading = false;
        this.errorMessage = httpErrorMessage(error, `Unable to load permissions for ${role.name}.`);
        this.renderNow();
      },
    });
  }

  openUserRolePermissions(event: Event, user: User): void {
    const role = this.roleRecords.find((item) => (user.roleIds ?? []).includes(item.id))
      ?? this.roleRecords.find((item) => (user.roles ?? [user.role]).includes(item.name));
    if (!role) {
      this.errorMessage = 'Assign a role before managing permissions.';
      this.renderNow();
      return;
    }
    this.openRolePermissions(event, role);
  }

  closeRolePermissions(): void {
    if (!this.permissionRole) return;
    this.permissionRole = null;
    this.rolePermissions = [];
    this.permissionDefinitions = [];
    this.selectedRolePermissionIds.clear();
    this.isRolePermissionsLoading = false;
    this.isEditingRolePermissions = false;
    this.renderNow();
  }

  toggleRolePermission(permissionId: string, enabled: boolean): void {
    const next = new Set(this.selectedRolePermissionIds);
    enabled ? next.add(permissionId) : next.delete(permissionId);
    this.selectedRolePermissionIds = next;
  }

  saveRolePermissions(): void {
    if (!this.permissionRole || !this.canEditPermissionRole) return;
    this.isSavingRolePermissions = true;
    this.errorMessage = '';
    this.rolesService.replacePermissions(this.permissionRole.id, [...this.selectedRolePermissionIds])
      .pipe(finalize(() => { this.isSavingRolePermissions = false; this.renderNow(); }))
      .subscribe({
        next: (ids) => {
          const selected = new Set(ids);
          this.selectedRolePermissionIds = selected;
          this.rolePermissions = this.permissionDefinitions.filter((permission) => selected.has(permission.id));
          this.rolePermissionCache.set(this.permissionRole!.id, this.rolePermissions);
          this.isEditingRolePermissions = false;
          this.successMessage = `${this.permissionRole!.name} permissions were updated.`;
          this.renderNow();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to update role permissions.');
          this.renderNow();
        },
      });
  }

  isLocationSelected(locationId: string): boolean {
    return this.invite.businessLocationIds.includes(locationId);
  }

  userLocationSummary(user: User): string {
    const names = (user.businessLocationIds ?? [])
      .map((id) => this.locations.find((location) => location.id === id)?.name)
      .filter((name): name is string => Boolean(name));
    return names.join(', ') || user.business || 'Not assigned';
  }

  loadRoles(): void {
    this.rolesService.list().subscribe({
      next: (roles) => {
        this.roleRecords = this.isPlatformAdminView
          ? roles.filter((role) => !['SuperAdmin', 'PlatformSuperAdmin'].includes(role.name))
          : roles;
        this.roles = this.roleRecords.map((role) => role.name);
        this.renderNow();
      },
      error: () => this.renderNow(),
    });
  }

  loadUsers(): void {
    this.isLoading = true;
    this.usersPageExhausted = false;
    this.errorMessage = '';

    this.usersService
      .listPage(1, this.userPageSize, false, this.selectedActiveFilter())
      .pipe(finalize(() => {
        this.isLoading = false;
        this.renderNow();
      }))
      .subscribe({
        next: (page) => {
          this.users = page.users;
          this.userPageNumber = page.pageNumber;
          this.totalUserCount = page.totalCount;
          this.usersPageExhausted = page.users.length < this.userPageSize || this.users.length >= page.totalCount;
          this.renderNow();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Users API is unavailable.');
          this.renderNow();
        },
      });
  }

  private loadMoreUsers(): void {
    this.isLoadingMore = true;
    this.usersService
      .listPage(this.userPageNumber + 1, this.userPageSize, false, this.selectedActiveFilter())
      .pipe(
        finalize(() => {
          this.isLoadingMore = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (page) => {
          const ids = new Set(this.users.map((user) => user.id));
          const newUsers = page.users.filter((user) => !ids.has(user.id));
          this.users = [...this.users, ...newUsers];
          this.userPageNumber = page.pageNumber;
          this.totalUserCount = page.totalCount;
          this.usersPageExhausted = page.users.length < this.userPageSize
            || newUsers.length === 0
            || this.users.length >= page.totalCount;
          this.cdr.detectChanges();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to load more users.');
          this.cdr.detectChanges();
        },
      });
  }

  inviteUser(): void {
    if (this.invite.roleIds.length === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }
    if (!this.isPlatformAdminView && this.invite.businessLocationIds.length === 0) {
      this.errorMessage = 'Select at least one business location.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.usersService
      .invite(this.invite)
      .pipe(finalize(() => {
        this.isSubmitting = false;
        this.renderNow();
      }))
      .subscribe({
        next: (user) => {
          this.users = [
            { ...user, businessLocationIds: [...this.invite.businessLocationIds] },
            ...this.users,
          ];
          this.invite = { name: '', email: '', roleIds: [], businessLocationIds: [], accessExpiresAt: '' };
          this.successMessage = user.invitationEmailSent
            ? 'User created and invitation email sent.'
            : 'User was created, but the invitation email could not be sent. Check the email service configuration.';
          this.renderNow();
        },
        error: (error: unknown) => {
          this.errorMessage = httpErrorMessage(error, 'Unable to invite user.');
          this.renderNow();
        },
      });
  }

  updateRole(user: User): void {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.editingRoleIds.length === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }

    if (!this.isPlatformAdminView && this.editingLocationIds.length === 0) {
      this.errorMessage = 'Select at least one business location.';
      return;
    }
    this.isSubmitting = true;
    const updateRequest: Observable<unknown> = this.isPlatformAdminView
      ? this.usersService.replaceRoles(user.id, this.editingRoleIds)
      : forkJoin({
          role: this.usersService.replaceRoles(user.id, this.editingRoleIds),
          locations: this.usersService.replaceBusinessLocations(user.id, this.editingLocationIds),
        });
    updateRequest.pipe(finalize(() => {
      this.isSubmitting = false;
      this.renderNow();
    })).subscribe({
      next: () => {
        user.businessLocationIds = [...this.editingLocationIds];
        user.roleIds = [...this.editingRoleIds];
        user.roles = this.roleRecords.filter((role) => this.editingRoleIds.includes(role.id)).map((role) => role.name);
        user.role = user.roles[0] ?? 'Unassigned';
        this.editingUserId = '';
        this.successMessage = `${user.name}'s role and location access were updated.`;
        this.renderNow();
      },
      error: (error: unknown) => {
        this.errorMessage = httpErrorMessage(error, 'Unable to update role.');
        this.renderNow();
      },
    });
  }

  beginEdit(user: User): void {
    this.editingUserId = user.id;
    this.editingLocationIds = [...(user.businessLocationIds ?? [])];
    this.editingRoleIds = [...(user.roleIds ?? [])];
    this.renderNow();
  }

  closeUserEdit(): void {
    this.editingUserId = '';
    this.editingLocationIds = [];
    this.editingRoleIds = [];
    this.renderNow();
  }

  beginAccessEdit(user: User): void {
    this.accessEditingUserId = user.id;
    this.accessExpiryValue = user.accessExpiresAt ? this.toLocalDateTime(user.accessExpiresAt) : '';
  }

  saveExpiry(user: User): void {
    const expiresAt = this.accessExpiryValue ? new Date(this.accessExpiryValue).toISOString() : null;
    this.runUserAction(user, this.usersService.updateAccess(user.id, user.status !== 'Suspended', expiresAt), () => {
      user.accessExpiresAt = expiresAt;
      this.accessEditingUserId = '';
      this.successMessage = expiresAt ? `Access expiry updated for ${user.name}.` : `Access expiry removed for ${user.name}.`;
    }, 'Unable to update user expiry.');
  }

  toggleUserStatus(user: User): void {
    const activate = user.status === 'Suspended';
    if (!activate && !window.confirm(`Deactivate ${user.name}? They will no longer be able to sign in.`)) return;
    this.runUserAction(user, this.usersService.updateAccess(user.id, activate, user.accessExpiresAt ?? null), () => {
      user.status = activate ? 'Active' : 'Suspended';
      this.successMessage = `${user.name} was ${activate ? 'activated' : 'deactivated'}.`;
    }, `Unable to ${activate ? 'activate' : 'deactivate'} user.`);
  }

  resendInvite(user: User): void {
    this.runUserAction(user, this.usersService.resendInvite(user.id), (result) => {
      user.invitationSentAt = result.invitationSentAt;
      this.successMessage = result.invitationEmailSent
        ? `A new invitation was sent to ${user.email}.`
        : 'A new temporary password was generated, but the email could not be sent.';
    }, 'Unable to resend the invitation.');
  }

  openDeleteUser(user: User): void {
    this.deletingUser = user;
    this.deleteConfirmationText = '';
    this.errorMessage = '';
  }

  closeDeleteUser(): void {
    if (this.processingUserId) return;
    this.deletingUser = null;
    this.deleteConfirmationText = '';
  }

  confirmDeleteUser(): void {
    const user = this.deletingUser;
    if (!user || this.deleteConfirmationText.trim().toLowerCase() !== user.email.trim().toLowerCase()) return;
    this.runUserAction(user, this.usersService.remove(user.id), () => {
      this.users = this.users.filter((item) => item.id !== user.id);
      this.totalUserCount = Math.max(0, this.totalUserCount - 1);
      this.successMessage = `${user.name} was removed.`;
      this.deletingUser = null;
      this.deleteConfirmationText = '';
    }, 'Unable to remove user.');
  }

  userStatus(user: User): string {
    if (user.status === 'Suspended') return 'Inactive';
    if (user.accessExpiresAt && new Date(user.accessExpiresAt) <= new Date()) return 'Expired';
    if (user.mustChangePassword) return 'Invited';
    return 'Active';
  }

  isMainOwner(user: User): boolean {
    return !this.isPlatformAdminView && this.currentUser.isBusinessOwner()
      && user.id === this.currentUser.user()?.id;
  }

  isProtectedPlatformAdmin(user: User): boolean {
    if (!this.isPlatformAdminView) return false;
    return (user.roles ?? [user.role]).some((role) => {
      const normalized = role.replace(/[\s_-]/g, '').toLowerCase();
      return normalized === 'superadmin' || normalized === 'platformsuperadmin';
    });
  }

  setAdminStatusFilter(filter: 'all' | 'active' | 'inactive'): void {
    if (this.adminStatusFilter === filter) return;
    this.adminStatusFilter = filter;
    this.users = [];
    this.totalUserCount = 0;
    this.userPageNumber = 1;
    this.usersPageExhausted = false;
    this.loadUsers();
  }

  private selectedActiveFilter(): boolean | undefined {
    if (!this.isPlatformAdminView || this.adminStatusFilter === 'all') return undefined;
    return this.adminStatusFilter === 'active';
  }

  expiryLabel(user: User): string {
    return user.accessExpiresAt
      ? new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(user.accessExpiresAt))
      : 'No expiry';
  }

  private runUserAction<T>(user: User, request: Observable<T>, success: (value: T) => void, fallback: string): void {
    this.processingUserId = user.id;
    this.errorMessage = '';
    this.successMessage = '';
    request.pipe(finalize(() => { this.processingUserId = ''; this.renderNow(); })).subscribe({
      next: (value) => { success(value); this.renderNow(); },
      error: (error: unknown) => { this.errorMessage = httpErrorMessage(error, fallback); this.renderNow(); },
    });
  }

  private toLocalDateTime(value: string): string {
    const date = new Date(value);
    const offset = date.getTimezoneOffset() * 60_000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  }

  toggleEditingLocation(locationId: string, checked: boolean): void {
    this.editingLocationIds = checked
      ? Array.from(new Set([...this.editingLocationIds, locationId]))
      : this.editingLocationIds.filter((id) => id !== locationId);
  }

  private renderNow(): void {
    this.cdr.detectChanges();
  }

}
